// Mock data for orders - realistic test data with communication protection
// Note: All emails, phones are fake/test data - no real contact attempts

export interface Contact {
	id: string
	uuid: string
	workspace_id: string
	display_name: string
	email: string | null
	phone_e164: string | null
	phone_raw: string | null
	preferred_contact_method: 'sms' | 'email' | 'phone' | 'facebook'
	facebook_id?: string | null
	whatsapp_number?: string | null
	preferred_channels: string[]
	tags: string[]
	last_activity_at: string
	last_order_at: string | null
	created_at: string
	updated_at: string
}

export interface Order {
	id: string
	uuid: string
	workspace_id: string
	contact_id: string
	location_id: string | null
	stage: 'new_lead' | 'qualified' | 'scheduled' | 'completed' | 'lost'
	loss_reason?: 'lost-contact' | 'scheduling-difficulty' | 'price' | 'insurance' | 'medicaid' | 'other'
	reason_detail?: string
	contacted_at: string | null
	qualified_at: string | null
	proposal_sent_at: string | null
	won_at: string | null
	lost_at: string | null
	is_new_customer: boolean | null
	campaign_id: string | null
	source: string
	// Attribution IDs moved to Order per PRD
	attribution: {
		gclid?: string
		fbclid?: string
		fbp?: string
		fbc?: string
	}
	value_cents: number
	created_at: string
	updated_at: string
}

export interface Activity {
	id: string
	uuid: string
	workspace_id: string
	order_id: string
	contact_id: string
	parent_activity_id: string | null
	type: 'call' | 'email' | 'text' | 'formSubmission' | 'note' | 'stageChange' | 'conversionSend' | 'message'
	direction: 'inbound' | 'outbound' | null
	summary: string | null
	payload_json: unknown
	call_recording_url: string | null
	transcript: string | null
	// Conversion fields added per PRD
	is_conversion: boolean
	conversion_type: 'contacted' | 'qualified' | 'proposal' | 'won' | null
	conversion_amount_cents: number | null
	conversion_currency: string | null
	platform: 'google' | 'facebook' | null
	// Delivery fields removed per PRD (no delivery_status, delivery_error, external_event_id)
	payload_hash: string | null
	// Message/Conversation specific fields
	message_content?: string
	message_channel?: 'sms' | 'email' | 'call' | 'whatsapp' | 'messenger'
	message_status?: 'sent' | 'delivered' | 'read' | 'failed'
	message_thread_id?: string
	has_attachments?: boolean
	attachment_urls?: string[]
	// Use created_at as conversion timestamp per PRD
	created_at: string
}

// Helper to generate dates
const daysAgo = (days: number) => new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString()
const hoursAgo = (hours: number) => new Date(Date.now() - hours * 60 * 60 * 1000).toISOString()
const minutesAgo = (minutes: number) => new Date(Date.now() - minutes * 60 * 1000).toISOString()

// Mock contacts with realistic names and safe test data
export const mockContacts: Contact[] = [
	{
		id: '1',
		uuid: 'contact-uuid-1',
		workspace_id: 'workspace-1',
		display_name: 'Sarah Johnson',
		email: 'sarah.test@example.com',
		phone_e164: '+15551234567',
		phone_raw: '(555) 123-4567',
		preferred_contact_method: 'sms',
		preferred_channels: ['phone', 'email'],
		tags: ['general'],
		last_activity_at: hoursAgo(2),
		last_order_at: hoursAgo(2),
		created_at: daysAgo(3),
		updated_at: hoursAgo(2),
	},
	{
		id: '2',
		uuid: 'contact-uuid-2',
		workspace_id: 'workspace-1',
		display_name: 'Michael Chen',
		email: 'michael.test@example.com',
		phone_e164: '+15551234568',
		phone_raw: '555-123-4568',
		preferred_contact_method: 'email',
		preferred_channels: ['email'],
		tags: ['ortho'],
		last_activity_at: daysAgo(1),
		last_order_at: null,
		created_at: daysAgo(5),
		updated_at: daysAgo(1),
	},
	{
		id: '3',
		uuid: 'contact-uuid-3',
		workspace_id: 'workspace-1',
		display_name: 'Emily Rodriguez',
		email: 'emily.test@example.com',
		phone_e164: '+15551234569',
		phone_raw: '(555) 123-4569',
		preferred_contact_method: 'phone',
		preferred_channels: ['phone'],
		tags: ['emergency'],
		last_activity_at: minutesAgo(30),
		last_order_at: minutesAgo(30),
		created_at: hoursAgo(6),
		updated_at: minutesAgo(30),
	},
	{
		id: '4',
		uuid: 'contact-uuid-4',
		workspace_id: 'workspace-1',
		display_name: 'David Thompson',
		email: 'david.test@example.com',
		phone_e164: '+15551234570',
		phone_raw: '555.123.4570',
		preferred_contact_method: 'facebook',
		facebook_id: 'david.thompson.test',
		preferred_channels: ['phone', 'text'],
		tags: ['implants'],
		last_activity_at: daysAgo(7),
		last_order_at: daysAgo(7),
		created_at: daysAgo(14),
		updated_at: daysAgo(7),
	},
	{
		id: '5',
		uuid: 'contact-uuid-5',
		workspace_id: 'workspace-1',
		display_name: 'Lisa Park',
		email: 'lisa.test@example.com',
		phone_e164: '+15551234571',
		phone_raw: '(555) 123-4571',
		preferred_contact_method: 'email',
		preferred_channels: ['email'],
		tags: ['general', 'cosmetic'],
		last_activity_at: hoursAgo(12),
		last_order_at: null,
		created_at: daysAgo(2),
		updated_at: hoursAgo(12),
	},
]

// Mock orders with realistic stages per PRD
// Mutable copy for position updates
let mutableOrders: Order[] = []

export const mockOrders: Order[] = [
	{
		id: '1',
		uuid: 'order-uuid-1',
		workspace_id: 'workspace-1',
		contact_id: '1',
		location_id: 'location-1',
		stage: 'qualified',
		contacted_at: daysAgo(2),
		qualified_at: hoursAgo(2),
		proposal_sent_at: null,
		won_at: null,
		lost_at: null,
		is_new_customer: true,
		campaign_id: 'campaign-dental-cleaning',
		source: 'Google Ads',
		attribution: {
			gclid: 'test-gclid-1',
			fbp: 'test-fbp-1'
		},
		value_cents: 25000, // $250 cleaning
		created_at: daysAgo(3),
		updated_at: hoursAgo(2),
	},
	{
		id: '2',
		uuid: 'order-uuid-2',
		workspace_id: 'workspace-1',
		contact_id: '2',
		location_id: 'location-1',
		stage: 'new_lead',
		contacted_at: null,
		qualified_at: null,
		proposal_sent_at: null,
		won_at: null,
		lost_at: null,
		is_new_customer: null,
		campaign_id: 'campaign-dental-cleaning',
		source: 'Google Ads',
		attribution: {
			gclid: 'test-gclid-2'
		},
		value_cents: 0,
		created_at: daysAgo(5),
		updated_at: daysAgo(1),
	},
	{
		id: '3',
		uuid: 'order-uuid-3',
		workspace_id: 'workspace-1',
		contact_id: '3',
		location_id: 'location-1',
		stage: 'scheduled',
		contacted_at: hoursAgo(4),
		qualified_at: hoursAgo(2),
		proposal_sent_at: hoursAgo(1),
		won_at: null,
		lost_at: null,
		is_new_customer: true,
		campaign_id: 'campaign-dental-emergency',
		source: 'Facebook Ads',
		attribution: {
			fbclid: 'test-fbclid-3',
			fbp: 'test-fbp-3'
		},
		value_cents: 150000, // $1500 emergency treatment
		created_at: hoursAgo(6),
		updated_at: minutesAgo(30),
	},
	{
		id: '4',
		uuid: 'order-uuid-4',
		workspace_id: 'workspace-1',
		contact_id: '4',
		location_id: 'location-1',
		stage: 'completed',
		contacted_at: daysAgo(13),
		qualified_at: daysAgo(12),
		proposal_sent_at: daysAgo(10),
		won_at: daysAgo(7),
		lost_at: null,
		is_new_customer: false,
		campaign_id: 'campaign-dental-orthodontics',
		source: 'Facebook Ads',
		attribution: {
			fbp: 'test-fbp-4',
			fbc: 'test-fbc-4'
		},
		value_cents: 500000, // $5000 orthodontics
		created_at: daysAgo(14),
		updated_at: daysAgo(7),
	},
	{
		id: '5',
		uuid: 'order-uuid-5',
		workspace_id: 'workspace-1',
		contact_id: '5',
		location_id: 'location-1',
		stage: 'lost',
		loss_reason: 'medicaid',
		reason_detail: 'Patient only has Medicaid coverage',
		contacted_at: hoursAgo(12),
		qualified_at: null,
		proposal_sent_at: null,
		won_at: null,
		lost_at: hoursAgo(12),
		is_new_customer: null,
		campaign_id: 'campaign-dental-whitening',
		source: 'Google Ads',
		attribution: {
			gclid: 'test-gclid-5'
		},
		value_cents: 0,
		created_at: daysAgo(2),
		updated_at: hoursAgo(12),
	},
]

// Mock activities for the orders (including conversations)
export const mockActivities: Activity[] = [
	// Order 1 (Sarah Johnson) - Qualified stage conversation
	{
		id: '1',
		uuid: 'activity-uuid-1',
		workspace_id: 'workspace-1',
		order_id: '1',
		contact_id: '1',
		parent_activity_id: null,
		type: 'call',
		direction: 'inbound',
		summary: 'Initial inquiry about dental cleaning',
		payload_json: { duration: 180, call_type: 'inquiry' },
		call_recording_url: 'https://test-recordings.example.com/call-1',
		transcript: 'Hi, I\'d like to schedule a dental cleaning appointment...',
		is_conversion: true,
		conversion_type: 'contacted',
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: daysAgo(3),
	},
	{
		id: '1a',
		uuid: 'activity-uuid-1a',
		workspace_id: 'workspace-1',
		order_id: '1',
		contact_id: '1',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Initial SMS inquiry',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi! I saw your ad about dental cleanings. Do you take my insurance? I have Blue Cross Blue Shield.',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-sarah-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: daysAgo(3),
	},
	{
		id: '1b',
		uuid: 'activity-uuid-1b',
		workspace_id: 'workspace-1',
		order_id: '1',
		contact_id: '1',
		parent_activity_id: null,
		type: 'message',
		direction: 'outbound',
		summary: 'Insurance verification response',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi Sarah! Yes, we do accept Blue Cross Blue Shield. I can help you schedule a cleaning appointment. What days work best for you?',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-sarah-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: new Date(new Date(daysAgo(3)).getTime() + 15 * 60 * 1000).toISOString(), // 15 minutes later
	},
	{
		id: '1c',
		uuid: 'activity-uuid-1c',
		workspace_id: 'workspace-1',
		order_id: '1',
		contact_id: '1',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Scheduling preference',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Great! I prefer mornings, maybe Tuesday or Wednesday next week?',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-sarah-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: new Date(new Date(daysAgo(3)).getTime() + 30 * 60 * 1000).toISOString(),
	},
	{
		id: '2',
		uuid: 'activity-uuid-2',
		workspace_id: 'workspace-1',
		order_id: '1',
		contact_id: '1',
		parent_activity_id: null,
		type: 'email',
		direction: 'outbound',
		summary: 'Sent appointment confirmation details',
		payload_json: { subject: 'Your dental appointment confirmation', template: 'appointment-confirmation' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi Sarah,\n\nGreat news! I\'ve scheduled your dental cleaning for Tuesday, March 12th at 9:00 AM.\n\nPlease arrive 15 minutes early to complete any necessary paperwork.\n\nBest regards,\nDental Care Team',
		message_channel: 'email',
		message_status: 'read',
		message_thread_id: 'thread-sarah-email-1',
		has_attachments: false,
		is_conversion: true,
		conversion_type: 'qualified',
		conversion_amount_cents: 25000,
		conversion_currency: 'USD',
		platform: null,
		payload_hash: null,
		created_at: hoursAgo(2),
	},

	// Order 2 (Michael Chen) - New stage, minimal conversation
	{
		id: '2a',
		uuid: 'activity-uuid-2a',
		workspace_id: 'workspace-1',
		order_id: '2',
		contact_id: '2',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Initial contact via email',
		payload_json: { channel: 'email' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hello, I\'m interested in scheduling a dental cleaning. Could you please send me information about your services and pricing?',
		message_channel: 'email',
		message_status: 'delivered',
		message_thread_id: 'thread-michael-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: daysAgo(1),
	},

	// Order 3 (Emily Rodriguez) - Proposal stage, active conversation
	{
		id: '3',
		uuid: 'activity-uuid-3',
		workspace_id: 'workspace-1',
		order_id: '3',
		contact_id: '3',
		parent_activity_id: null,
		type: 'formSubmission',
		direction: null,
		summary: 'Emergency appointment request form submitted',
		payload_json: { 
			form_type: 'emergency_request',
			pain_level: 8,
			symptoms: 'Severe tooth pain, swelling'
		},
		call_recording_url: null,
		transcript: null,
		is_conversion: true,
		conversion_type: 'contacted',
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: hoursAgo(6),
	},
	{
		id: '3a',
		uuid: 'activity-uuid-3a',
		workspace_id: 'workspace-1',
		order_id: '3',
		contact_id: '3',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Emergency SMS',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'URGENT: I have severe tooth pain and swelling on the right side of my face. Can you see me today?? 😰',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-emily-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: hoursAgo(5),
	},
	{
		id: '3b',
		uuid: 'activity-uuid-3b',
		workspace_id: 'workspace-1',
		order_id: '3',
		contact_id: '3',
		parent_activity_id: null,
		type: 'message',
		direction: 'outbound',
		summary: 'Emergency response',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi Emily, I\'m so sorry to hear you\'re in pain! We can definitely see you today. Can you come in at 2:30 PM? I\'m calling you now to discuss the details.',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-emily-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: new Date(new Date(hoursAgo(4)).getTime() + 10 * 60 * 1000).toISOString(),
	},
	{
		id: '4',
		uuid: 'activity-uuid-4',
		workspace_id: 'workspace-1',
		order_id: '3',
		contact_id: '3',
		parent_activity_id: null,
		type: 'call',
		direction: 'outbound',
		summary: 'Emergency consultation call',
		payload_json: { duration: 240, proposal_sent: true },
		call_recording_url: 'https://test-recordings.example.com/call-3',
		transcript: 'We can treat your emergency condition. Based on the X-rays, you need a root canal and crown...',
		is_conversion: true,
		conversion_type: 'proposal',
		conversion_amount_cents: 150000,
		conversion_currency: 'USD',
		platform: null,
		payload_hash: null,
		created_at: hoursAgo(3),
	},
	{
		id: '3c',
		uuid: 'activity-uuid-3c',
		workspace_id: 'workspace-1',
		order_id: '3',
		contact_id: '3',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Treatment acceptance',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Thank you for seeing me so quickly! The pain is much better now. Yes, I\'d like to proceed with the root canal and crown. When can we schedule it?',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-emily-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: minutesAgo(45),
	},
	{
		id: '3d',
		uuid: 'activity-uuid-3d',
		workspace_id: 'workspace-1',
		order_id: '3',
		contact_id: '3',
		parent_activity_id: null,
		type: 'message',
		direction: 'outbound',
		summary: 'Treatment scheduling',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Perfect! I have availability this Friday at 10 AM or Monday at 2 PM. The procedure will take about 90 minutes. Which works better for you?',
		message_channel: 'sms',
		message_status: 'delivered',
		message_thread_id: 'thread-emily-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: minutesAgo(30),
	},

	// Order 4 (David Thompson) - Won stage, completed conversation
	{
		id: '5',
		uuid: 'activity-uuid-5',
		workspace_id: 'workspace-1',
		order_id: '4',
		contact_id: '4',
		parent_activity_id: null,
		type: 'stageChange',
		direction: null,
		summary: 'Order won - treatment completed',
		payload_json: { from_stage: 'proposal', to_stage: 'won' },
		call_recording_url: null,
		transcript: null,
		is_conversion: true,
		conversion_type: 'won',
		conversion_amount_cents: 500000,
		conversion_currency: 'USD',
		platform: null,
		payload_hash: null,
		created_at: daysAgo(7),
	},
	{
		id: '4a',
		uuid: 'activity-uuid-4a',
		workspace_id: 'workspace-1',
		order_id: '4',
		contact_id: '4',
		parent_activity_id: null,
		type: 'message',
		direction: 'outbound',
		summary: 'Follow-up after treatment',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi David! How are you feeling after your orthodontic treatment? Any discomfort or questions?',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-david-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: daysAgo(6),
	},
	{
		id: '4b',
		uuid: 'activity-uuid-4b',
		workspace_id: 'workspace-1',
		order_id: '4',
		contact_id: '4',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Treatment feedback',
		payload_json: { channel: 'sms' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Feeling great! The braces are working perfectly. Thank you so much for the excellent care. I\'ll see you at my next appointment.',
		message_channel: 'sms',
		message_status: 'read',
		message_thread_id: 'thread-david-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: new Date(new Date(daysAgo(6)).getTime() + 30 * 60 * 1000).toISOString(),
	},

	// Order 5 (Lisa Park) - Lost stage, brief conversation
	{
		id: '5a',
		uuid: 'activity-uuid-5a',
		workspace_id: 'workspace-1',
		order_id: '5',
		contact_id: '5',
		parent_activity_id: null,
		type: 'message',
		direction: 'inbound',
		summary: 'Whitening inquiry',
		payload_json: { channel: 'email' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi, I\'m interested in teeth whitening services. What are your prices and do you accept Medicaid?',
		message_channel: 'email',
		message_status: 'read',
		message_thread_id: 'thread-lisa-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: daysAgo(2),
	},
	{
		id: '5b',
		uuid: 'activity-uuid-5b',
		workspace_id: 'workspace-1',
		order_id: '5',
		contact_id: '5',
		parent_activity_id: null,
		type: 'message',
		direction: 'outbound',
		summary: 'Insurance information',
		payload_json: { channel: 'email' },
		call_recording_url: null,
		transcript: null,
		message_content: 'Hi Lisa,\n\nThank you for your interest in our whitening services. Unfortunately, teeth whitening is considered a cosmetic procedure and is not typically covered by Medicaid.\n\nOur in-office whitening is $450, and we offer payment plans. Would you like to discuss other options?\n\nBest regards,\nDental Care Team',
		message_channel: 'email',
		message_status: 'read',
		message_thread_id: 'thread-lisa-1',
		has_attachments: false,
		is_conversion: false,
		conversion_type: null,
		conversion_amount_cents: null,
		conversion_currency: null,
		platform: null,
		payload_hash: null,
		created_at: hoursAgo(12),
	}
]

// Helper function to get order with contact data for UI display
export interface OrderWithContact extends Order {
	contact: Contact
	lastActivity?: Activity
}

// Initialize mutable orders on first access
const ensureMutableOrders = () => {
	if (mutableOrders.length === 0) {
		mutableOrders = JSON.parse(JSON.stringify(mockOrders))
	}
	return mutableOrders
}

export const getOrdersWithContacts = (): OrderWithContact[] => {
	const orders = ensureMutableOrders()
	return orders.map(order => {
		const contact = mockContacts.find(c => c.id === order.contact_id)!
		const lastActivity = mockActivities
			.filter(a => a.order_id === order.id)
			.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())[0]

		return {
			...order,
			contact,
			lastActivity
		}
	})
}

// Summary stats for dashboard
export const getOrdersSummary = () => {
	const orders = getOrdersWithContacts()
	const total = orders.length
	const qualified = orders.filter(o => ['qualified', 'scheduled', 'completed'].includes(o.stage)).length
	const converted = orders.filter(o => ['scheduled', 'completed'].includes(o.stage)).length
	const won = orders.filter(o => o.stage === 'completed').length
	
	return {
		total,
		qualified,
		converted,
		won,
		qualificationRate: total > 0 ? Math.round((qualified / total) * 100) : 0,
		conversionRate: total > 0 ? Math.round((converted / total) * 100) : 0,
		winRate: total > 0 ? Math.round((won / total) * 100) : 0,
	}
}

// Conversation helpers
export interface Conversation {
	id: string
	contact_id: string
	order_id: string
	thread_id: string
	channel: 'sms' | 'email' | 'call' | 'whatsapp' | 'messenger'
	last_message_at: string
	unread_count: number
	messages: Activity[]
}

export const getConversationsForOrder = (orderId: string): Conversation[] => {
	const orderActivities = mockActivities.filter(a => a.order_id === orderId && a.type === 'message')
	const threadGroups = orderActivities.reduce((groups, activity) => {
		const threadId = activity.message_thread_id || `thread-${activity.contact_id}`
		if (!groups[threadId]) {
			groups[threadId] = []
		}
		groups[threadId].push(activity)
		return groups
	}, {} as Record<string, Activity[]>)

	return Object.entries(threadGroups).map(([threadId, messages]) => {
		const sortedMessages = messages.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
		const lastMessage = sortedMessages[sortedMessages.length - 1]
		const unreadCount = sortedMessages.filter(m => m.message_status !== 'read' && m.direction === 'inbound').length

		return {
			id: threadId,
			contact_id: lastMessage.contact_id,
			order_id: orderId,
			thread_id: threadId,
			channel: lastMessage.message_channel || 'sms',
			last_message_at: lastMessage.created_at,
			unread_count: unreadCount,
			messages: sortedMessages
		}
	})
}

export const getAllConversations = (): Conversation[] => {
	const allConversations: Conversation[] = []
	
	mockOrders.forEach(order => {
		const conversations = getConversationsForOrder(order.id)
		allConversations.push(...conversations)
	})

	// Sort by last message time (most recent first)
	return allConversations.sort((a, b) => new Date(b.last_message_at).getTime() - new Date(a.last_message_at).getTime())
}

export const getUnreadConversationsCount = (): number => {
	return getAllConversations().reduce((total, conv) => total + conv.unread_count, 0)
}

export const getConversationByThreadId = (threadId: string): Conversation | undefined => {
	return getAllConversations().find(conv => conv.thread_id === threadId)
}

// Function to update order position in the mutable store
export const updateOrderPosition = (orderId: string, _newPosition: number): void => {
	const orders = ensureMutableOrders()
	const orderIndex = orders.findIndex(order => order.id === orderId)
	if (orderIndex !== -1) {
		orders[orderIndex].updated_at = new Date().toISOString()
	}
}

// Mock data export for backward compatibility
export const mockLeadsData = getOrdersWithContacts()
